import { useCallback, useEffect, useRef } from "react";
import { useAgent } from "@copilotkit/react-core/v2";
import { SquarePen } from "lucide-react";
import { getChatHistory, setConversationInactive } from "@/services/chatHistoryService";
import { findChatScroller } from "./chatScroll";
import { mapHistoryToAgentMessages } from "./mapHistoryToAgentMessages";
import { useCopilotSidebarOpenMarker } from "./copilot-sidebar-open-marker";

/**
 * Sidebar-header controls: a "New chat" button + the infinite-scroll loader
 * for older history. Both live here because both mutate the same conversation
 * and share the pagination cursor (New chat must reset it).
 *
 * Mounted inside the CopilotSidebar header slot, so it has the chat config
 * context and the live message-scroll container in the DOM.
 *
 * Renders the New chat button; the infinite scroll runs as a side effect.
 */

const PAGE_SIZE = 50;
const NEAR_TOP_PX = 140;

export function CopilotChatHeaderControls() {
  const { agent } = useAgent({ agentId: "default" });
  // Lives here because the header slot is inside the sidebar's own chat-config provider.
  useCopilotSidebarOpenMarker();
  // nextPage: the page to fetch on the next scroll-to-top (page 1 is already
  // loaded by the welcome restore). done: no older messages left.
  const pager = useRef({ nextPage: 2, loading: false, done: false });

  // ── Infinite scroll: load older pages when the user scrolls near the top ──
  // Listen on `document` in the capture phase rather than attaching to the
  // scroll container directly: the container renders a tick AFTER the sidebar
  // header mounts (and `scroll` doesn't bubble), so a direct attach races the
  // async history restore. Capturing on document is race-free; we filter to
  // the chat scroller by event target.
  useEffect(() => {
    if (!agent) return;

    const onScroll = async (e: Event) => {
      const scroller = findChatScroller();
      if (!scroller || e.target !== scroller) return;
      const p = pager.current;
      if (p.loading || p.done) return;
      if (scroller.scrollTop > NEAR_TOP_PX) return;
      if (agent.messages.length === 0) return; // nothing restored yet

      p.loading = true;
      try {
        const { messages, total } = await getChatHistory(p.nextPage, PAGE_SIZE);
        const existing = new Set(agent.messages.map((m) => m.id));
        const filtered = messages.filter((m) => !existing.has(m.pid));
        const older = mapHistoryToAgentMessages(filtered);

        if (older.length === 0) {
          p.done = true;
          return;
        }

        const prevHeight = scroller.scrollHeight;
        agent.setMessages([...older, ...agent.messages]);
        p.nextPage += 1;
        if (agent.messages.length >= total) p.done = true;

        // Preserve the viewport anchor after the prepend grows the list (also
        // moves scrollTop away from 0, preventing a cascade re-trigger).
        requestAnimationFrame(() => {
          scroller.scrollTop += scroller.scrollHeight - prevHeight;
        });
      } catch {
        /* transient — allow retry on the next scroll */
      } finally {
        p.loading = false;
      }
    };

    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => document.removeEventListener("scroll", onScroll, { capture: true });
  }, [agent]);

  // ── New chat: archive current conversation, clear the buffer ──
  const newChat = useCallback(async () => {
    if (!agent) return;
    try {
      await setConversationInactive();
    } catch {
      /* even if the archive call fails, clear the local view */
    }
    pager.current = { nextPage: 2, loading: false, done: false };
    if (agent.isRunning) agent.abortRun();
    agent.setMessages([]); // empties the buffer → welcome screen returns
  }, [agent]);

  return (
    <button
      type="button"
      onClick={newChat}
      title="New chat"
      aria-label="Start a new chat"
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <SquarePen className="h-4 w-4" />
    </button>
  );
}
